import React, { useState, useEffect } from "react";
import { PageLoader } from "../../Loaders/PageLoader";
import cummonStyles from "../SettingModal.module.scss";
import styles from "./AccountSettings.module.scss";
import axios from "axios";
import { Loader } from "../../Loaders/Loader";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

import type { NoticeType } from "../../../../types/settingsTypes";
import { useTranslation } from "react-i18next";

type Props = {
  client: string;
  memberId: number;
  setNotice: React.Dispatch<React.SetStateAction<NoticeType>>;
  changeModalCss?: (width: string, height: string) => void;
  setIsMenuOpen?: (isOpen: boolean) => void;
  setHeaderShow?: (show: boolean) => void;
};

type MemberInfo = {
  name?: string;
  email?: string;
  current_password?: string;
  password?: string;
  password_confirmation?: string;
};

type Mode = "default" | "deleteAccount";

const AccountSettings = ({ client, setNotice, memberId, changeModalCss, setIsMenuOpen, setHeaderShow }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>("default");
  const [memberInfo, setMemberInfo] = useState<MemberInfo>({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    current_password: "",
  });
  const [deletionConfirmationValue, setDeletionConfirmationValue] = useState<string>("");
  const [destroyErrorMessage, setDestroyErrorMessage] = useState<string>("");
  const [isGoogleSsoAuthenticated, setIsGoogleSsoAuthenticated] = useState<boolean>(false);
  const [formLoadings, setFormLoadings] = useState<{ member: boolean; password: boolean }>({
    member: false,
    password: false,
  });

  const getMemberData = async () => {
    const endpoint = `/${client}/settings/members/${memberId}`;
    const { data } = await axios.get(endpoint);

    setMemberInfo({ name: data.name, email: data.email });
    setIsGoogleSsoAuthenticated(data.is_google_sso_authenticated);
    setLoading(false);
  };

  const updateMember = async () => {
    const endpoint = `/${client}/settings/members/${memberId}`;
    setFormLoadings((prev) => ({ ...prev, member: true }));
    const results = await axios
      .patch(endpoint, {
        member: {
          name: memberInfo.name,
          email: memberInfo.email,
        },
      })
      .catch((error) => {
        setNotice({
          message: error.response.data?.message || t("AccountSettings.更新に失敗しました。"),
          type: "alert",
        });
      });

    if (results?.data) {
      setMemberInfo({ name: results.data.name, email: results.data.email });
      setNotice({ message: results.data.message, type: "success" });
    }
    setFormLoadings((prev) => ({ ...prev, member: false }));
  };

  const updatePassword = async () => {
    const endpoint = `/${client}/settings/members/${memberId}`;

    setFormLoadings((prev) => ({ ...prev, password: true }));
    await axios
      .patch(endpoint, {
        member: {
          current_password: memberInfo.current_password,
          password: memberInfo.password,
          password_confirmation: memberInfo.password_confirmation,
        },
      })
      .then((res) => {
        setNotice({ message: res.data.message, type: "success" });
      })
      .catch((error) => {
        setNotice({
          message: error.response.data?.message || t("AccountSettings.更新に失敗しました。"),
          type: "alert",
        });
      });
    setFormLoadings((prev) => ({ ...prev, password: false }));
  };

  const destroyAccount = async () => {
    if (!deletionConfirmationValue) {
      return;
    }

    const endpoint = `/${client}/settings/members/${memberId}`;
    await axios
      .delete(endpoint, {
        data: {
          deletion_confirmation_value: deletionConfirmationValue,
        },
      })
      .then((res) => {
        setNotice({
          message: res.data.message,
          type: "success",
        });
      })
      .catch((error) => {
        if (error.response) {
          const message = error.response.data.message;
          setDestroyErrorMessage(message);
        }
      });
  };

  const resetPassword = async () => {
    const endpoint = `/${client}/members/${memberId}/send_reset_password_mail`;

    await axios.get(endpoint).then((res) => {
      setNotice({
        message: res.data.message,
        type: "success",
      });
    });
  };

  useEffect(() => {
    getMemberData();
  }, []);

  const renderContent = () => {
    switch (mode) {
      case "deleteAccount":
        return (
          <div className={cummonStyles.popup}>
            <h1 className={cummonStyles.popupTitle}>{t("AccountSettings.アカウントの削除")}</h1>
            <p className={cummonStyles.text}>
              {t("AccountSettings.所属するワークスペースのマイプロジェクト内にある全てのデータを削除します。")}
              <br />
              {t("AccountSettings.削除したデータを復元することはできません。")}
              <br />
              <br />
              {t(
                "AccountSettings.退会が完了すると所属する全てのワークスペースからアカウントが削除され、ログインできなくなります。",
              )}
              <br />
              {t("AccountSettings.今一度ご確認のうえ、実行してください。")}
            </p>
            <div className={styles.modalForm}>
              {isGoogleSsoAuthenticated ? (
                <>
                  <span>{t("AccountSettings.enter_mail_address")}</span>
                  <input type="email" onChange={(e) => setDeletionConfirmationValue(e.target.value)} />
                </>
              ) : (
                <>
                  <span>{t("AccountSettings.確認のため「パスワード」を入力してください")}</span>
                  <input type="password" onChange={(e) => setDeletionConfirmationValue(e.target.value)} />
                </>
              )}
              {destroyErrorMessage && <p className={cummonStyles.errorMessage}>{destroyErrorMessage}</p>}
            </div>
            <div className={styles.modalBtnContent}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setMode("default");
                  setIsMenuOpen?.(true);
                  changeModalCss?.("100%", "auto");
                  setHeaderShow?.(true);
                }}
              >
                {t("AccountSettings.キャンセル")}
              </button>
              <button
                className={`btn btn-secondary bg-danger border-danger ${styles.deleteBtn}`}
                onClick={destroyAccount}
              >
                {t("AccountSettings.アカウント削除")}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <>
            <h1 className={`${cummonStyles.title} ${cummonStyles.mb32}`}>{t("AccountSettings.アカウント設定")}</h1>
            <div className={cummonStyles.mb32}>
              <div className={styles.section}>
                <div className={styles.sectionTitle}>{t("AccountSettings.アカウント情報")}</div>
                <div className={styles.form}>
                  <div className={styles.formItem}>
                    <span className={styles.formLabel}>{t("AccountSettings.アカウント名")}</span>
                    <input
                      type="text"
                      value={memberInfo.name}
                      onChange={(e) => setMemberInfo((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className={styles.formItem}>
                    <span className={styles.formLabel}>{t("AccountSettings.メールアドレス")}</span>
                    <input
                      type="text"
                      value={memberInfo.email}
                      onChange={(e) => setMemberInfo((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <button className={`btn btn-info btn-sm-size ${styles.formBtn}`} onClick={updateMember}>
                    {!formLoadings.member ? t("AccountSettings.変更する") : <Loader />}
                  </button>
                </div>
              </div>
            </div>
            {!isGoogleSsoAuthenticated && (
              <div className={cummonStyles.mb32}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>{t("AccountSettings.パスワード")}</div>
                  <div className={styles.form}>
                    <>
                      <div className={styles.formItem}>
                        <span className={styles.formLabel}>{t("AccountSettings.現在のパスワード")}</span>
                        <input
                          type="password"
                          value={memberInfo.current_password}
                          onChange={(e) => setMemberInfo((prev) => ({ ...prev, current_password: e.target.value }))}
                        />
                      </div>
                      <div className={styles.formItem}>
                        <span className={styles.formLabel}>{t("AccountSettings.新しいパスワード")}</span>
                        <input
                          type="password"
                          value={memberInfo.password}
                          onChange={(e) => setMemberInfo((prev) => ({ ...prev, password: e.target.value }))}
                        />
                      </div>
                      <div className={styles.formItem}>
                        <span className={styles.formLabel}>{t("AccountSettings.確認用パスワード")}</span>
                        <input
                          type="password"
                          value={memberInfo.password_confirmation}
                          onChange={(e) =>
                            setMemberInfo((prev) => ({ ...prev, password_confirmation: e.target.value }))
                          }
                        />
                      </div>
                      <button className={`btn btn-info btn-sm-size ${styles.formBtn}`} onClick={updatePassword}>
                        {!formLoadings.password ? t("AccountSettings.変更する") : <Loader />}
                      </button>
                      <p className={styles.smallLink} style={{ cursor: "pointer" }} onClick={resetPassword}>
                        {t("AccountSettings.現在のパスワードがわからない場合はこちら")}
                      </p>
                    </>
                  </div>
                </div>
              </div>
            )}
            <p
              className={styles.deleteMessage}
              onClick={() => {
                setMode("deleteAccount");
                setIsMenuOpen?.(false);
                changeModalCss?.("765px", "auto");
                setHeaderShow?.(false);
              }}
            >
              {t("AccountSettings.アカウントの削除はこちら")}
            </p>
          </>
        );
    }
  };

  return <>{!loading ? renderContent() : <PageLoader loadingHeight={"50vh"} />}</>;
};

export default AccountSettings;
